import { api } from "@/lib/api";
import type {
  Env,
  Pagination,
  PresenceRealtime,
  PresenceSession,
  PresenceSummary,
  PresenceTopUser,
  PresenceTrends,
  PresenceUsage,
} from "@/lib/types";
import { useQuery } from "@tanstack/react-query";

// IR-1148: the Presence dashboard scopes every read to the selected env.
// Backend reads ?env=<dev|prod> and routes to the matching DB alias
// (default prod). Forward env on every call so dev/prod don't mix.
const POLL_60S = { refetchInterval: 60_000 };
const envQ = (env: Env) => `?env=${env}`;

export function usePresenceRealtime(env: Env = "prod") {
  return useQuery({
    queryKey: ["presence-realtime", env],
    queryFn: () => api<{ data: PresenceRealtime }>(`/api/ops/v1/presence/realtime${envQ(env)}`),
    refetchInterval: 15_000,
  });
}

export function usePresenceUsage(env: Env = "prod") {
  return useQuery({
    queryKey: ["presence-usage", env],
    queryFn: () => api<{ data: PresenceUsage }>(`/api/ops/v1/presence/usage${envQ(env)}`),
    ...POLL_60S,
  });
}

export function usePresenceSummary(env: Env = "prod") {
  return useQuery({
    queryKey: ["presence-summary", env],
    queryFn: () =>
      api<{ data: PresenceSummary }>(`/api/ops/v1/presence/sessions/summary${envQ(env)}`),
    ...POLL_60S,
  });
}

export function usePresenceTrends(env: Env, metric: string, range: string) {
  return useQuery({
    queryKey: ["presence-trends", env, metric, range],
    queryFn: () =>
      api<{ data: PresenceTrends }>(
        `/api/ops/v1/presence/trends?env=${env}&metric=${metric}&range=${range}`,
      ),
    ...POLL_60S,
  });
}

export function usePresenceTopUsers(env: Env = "prod") {
  return useQuery({
    queryKey: ["presence-top", env],
    queryFn: () =>
      api<{ data: PresenceTopUser[] }>(`/api/ops/v1/presence/users/top?env=${env}&by=frequent`),
    ...POLL_60S,
  });
}

export function usePresenceSessions(
  env: Env,
  opts: { offset?: number; limit?: number; accountId?: number } = {},
) {
  const { offset = 0, limit = 50, accountId } = opts;
  const acct = accountId != null ? `&account_id=${accountId}` : "";
  return useQuery({
    queryKey: ["presence-sessions", env, offset, limit, accountId],
    queryFn: () =>
      api<{ data: PresenceSession[]; pagination: Pagination }>(
        `/api/ops/v1/presence/sessions/explorer?env=${env}&offset=${offset}&limit=${limit}${acct}`,
      ),
  });
}
